import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedListWidget } from '../../dashboard-basic-view/components/RankedListWidget/ranked-list-widget';
import { NoDataWidget } from '../../dashboard-basic-view/components/NoDataWidget/no-data-widget';
import { formatNumber } from '../../../reporting/components/portfolio-report';
import { ReportingPaths } from '../../../reporting/reporting.router';
import { PortfolioWidgetProps } from './portfolio-widget.types';

// Portfolio valuation (yield method), shown in kNOK (thousands). Only buildings with an uploaded
// financial report carry a valuation, so this mirrors the register-based report's gating: when no
// building qualifies the widget shows the no-data state.
export function PortfolioValuationWidget({ data, isLoading }: PortfolioWidgetProps) {
  const { t } = useTranslation('widget');

  const { total, highest, lowest } = useMemo(() => {
    const withValuation = (data?.buildings ?? []).filter((b) => b.yieldValuation != null);

    // Headline: total portfolio valuation; lists: each building's valuation. Both in kNOK.
    const total = withValuation.reduce((sum, b) => sum + (b.yieldValuation as number), 0);

    const values = withValuation.map((b) => ({
      id: b.id,
      label: b.name,
      value: Math.round((b.yieldValuation as number) / 1000),
    }));

    return {
      total,
      highest: [...values].sort((a, b) => b.value - a.value).slice(0, 3),
      lowest: [...values].sort((a, b) => a.value - b.value).slice(0, 3),
    };
  }, [data]);

  if (!isLoading && highest.length === 0 && lowest.length === 0)
    return <NoDataWidget title={t('portfolioValuation.title')} noDataText={t('portfolioValuation.noData')} />;

  return (
    <RankedListWidget
      to={ReportingPaths.FinancialOverview}
      title={t('portfolioValuation.title')}
      tooltip={t('portfolioValuation.tooltip')}
      value={formatNumber(total / 1000)}
      unit={t('portfolioValuation.unit')}
      rankUnit={t('portfolioValuation.rankUnit')}
      loading={isLoading || !data}
      highListTitle={t('portfolioValuation.highest')}
      lowListTitle={t('portfolioValuation.lowest')}
      highListData={highest}
      lowListData={lowest}
    />
  );
}
